import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useToast } from "../../components/ToastContext";
import { api } from '../../services/api';

const SetupDialog = ({ open, onClose, setupData, onSuccess }) => {
  const theme = useTheme();
  const toast = useToast();
  const userId = sessionStorage.getItem('userId');

  const [formData, setFormData] = useState({
    setupKey: '',
    setupKeyValue: '',
    isActive: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setErrors({}); 
    
    if (open && setupData) {
      setFormData({
        setupKey: setupData.setupKey || '',
        setupKeyValue: setupData.setupKeyValue || '',
        isActive: setupData.isActive || false,
      });
    } else if (open && !setupData) {
      setFormData({
        setupKey: '',
        setupKeyValue: '',
        isActive: false,
      });
    }
  }, [open, setupData]);

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
    
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const getErrorMessage = (error) => {
    if (!error.response) return "Network error. Please check your connection.";
    const { status, data } = error.response;
    if (status === 401) return "Unauthorized. Please login again.";
    if (status === 403) return "You don't have permission to perform this action.";
    if (status >= 500) return "Server error. Please try again later.";
    return data?.message || `Error: ${status}`;
  };

  const handleSubmit = async () => {
    let newErrors = {};
    if (!formData.setupKey.trim()) newErrors.setupKey = "Setup Key is required.";
    if (!formData.setupKeyValue.trim()) newErrors.setupKeyValue = "Setup Value is required.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all the required fields.");
      return;
    }

    try {
      setLoading(true);
      let response;
      const headers = {
        'X-CORRELATION-ID': 'e7707845-953b-4baa-8dea-260aa7dfc8f9',
        'Content-Type': 'application/json',
      };

      if (setupData) {
        const updatePayload = {
          SetupId: setupData.setupId,
          SetupKey: formData.setupKey.trim(),
          SetupKeyValue: formData.setupKeyValue.trim(),
          CreatedBy: setupData.createdBy,
          CreatedOn: setupData.createdOn,
          UpdatedBy: Number.parseInt(userId, 10),
          UpdatedOn: new Date().toISOString(),
          IsActive: formData.isActive,
        };
        response = await api.post('/Project/UpdateSetupDetails', updatePayload, { headers });
      } else {
        const createPayload = {
          SetupKey: formData.setupKey.trim(),
          SetupKeyValue: formData.setupKeyValue.trim(),
          CreatedBy: Number.parseInt(userId, 10),
          CreatedOn: new Date().toISOString(),
          UpdatedBy: Number.parseInt(userId, 10),
          UpdatedOn: new Date().toISOString(),
          IsActive: formData.isActive,
        };
        response = await api.post('/Project/CreateSetupDetails', createPayload, { headers });
      }

      if (response.status === 200 || response.status === 201) {
        toast.success(`Setup configuration ${setupData ? 'updated' : 'added'} successfully!`);
        onSuccess();
        handleClose();
      }
    } catch (error) {
      console.error("Error saving setup:", error);
      
      const apiErrorMessage = error.response?.data?.errorMessages?.[0]?.message;
      
      if (apiErrorMessage) {
        setErrors({ setupKey: apiErrorMessage });
      } else {
        toast.error(getErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: theme.palette.background.default, pb: 1, mb: 2 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: "text.primary" }}>
          {setupData ? 'Edit Setup' : 'Add New Setup'}
        </Typography>
        {setupData && (
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Update the selected setup entry without changing its functional flow.
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ bgcolor: theme.palette.background.paper }}>
        <TextField
          margin="dense"
          label="Setup Key *"
          placeholder="Enter Setup key"
          type="text"
          fullWidth
          value={formData.setupKey}
          onChange={handleInputChange('setupKey')}
          error={!!errors.setupKey}
          helperText={errors.setupKey}
          sx={{ mb: 2, mt: 1 }}
        />
        <TextField
          margin="dense"
          label="Setup Value *"
          placeholder="Enter Setup value"
          type="text"
          fullWidth
          value={formData.setupKeyValue}
          onChange={handleInputChange('setupKeyValue')}
          error={!!errors.setupKeyValue}
          helperText={errors.setupKeyValue}
          sx={{ mb: 1.5 }}
        />
        <Box sx={{ mt: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label={<Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "text.primary" }}>Active</Typography>}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, bgcolor: theme.palette.background.default }}>
        <Button onClick={handleClose} variant="outlined"
          sx={{
            p: 0.7,
            px: 2,
            color: theme.palette.mode === 'dark' ? 'grey.300' : 'text.secondary',
            borderColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.400',
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
              borderColor: theme.palette.mode === 'dark' ? 'grey.500' : 'grey.600',
            }
          }} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" sx={{ py: 1.2 }} disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetupDialog;